<template>
  <div class="privacy">
    <h1>Privacy Policy</h1>
    <p><strong>Effective Date:</strong> 25/12/24</p>

    <p>
      Welcome to Cognify, an AI-powered app designed to assist students with note-taking by generating notes from scanned documents, imported files, voice notes, and YouTube videos. Protecting your privacy is important to us, and we aim to be transparent about how we handle your data.
    </p>

    <h2>1. Information We Collect</h2>
    <h3>1.1 Information You Provide</h3>
    <ul>
      <li>
        <strong>Anonymous Account Information:</strong> When you use the app, we create an anonymous Firebase account for you. No personally identifiable information (e.g., name or email) is required or stored for account creation.
      </li>
      <li>
        <strong>Uploaded Content:</strong> When you upload files such as documents, photos, or audio recordings, the content is processed temporarily. We extract only the text from these files for the purpose of reformatting it using OpenAI’s API. <strong>These files are not stored on our servers.</strong>
      </li>
      <li><strong>Feedback and Support Requests:</strong> Any communication or feedback you send to us.</li>
    </ul>

    <h3>1.2 Automatically Collected Information</h3>
    <ul>
      <li><strong>Usage Data:</strong> Information about your interactions with the app, including features used, time spent, and preferences.</li>
      <li>
        <strong>Analytics Data:</strong> We use Firebase Analytics to track app performance and user trends. <strong>This data is anonymized and aggregated.</strong>
      </li>
      <li><strong>Device Information:</strong> Device type, operating system, IP address (temporarily by Firebase and not stored), and app version.</li>
      <li><strong>Advertising Data:</strong> AdMob collects data related to your interactions with ads to personalize and serve advertisements. <strong>Users in the EEA and UK will be asked for consent before AdMob collects data.</strong></li>
      <li><strong>Cookies and Tracking Technologies:</strong> Cookies and local storage to improve user experience and analyze app performance.</li>
    </ul>

    <h3>1.3 Third-Party Data</h3>
    <ul>
      <li>
        <strong>Text Processing:</strong> Extracted text from uploaded content is sent to OpenAI’s API for reformatting and note generation. <strong>OpenAI’s handling of this data is subject to their privacy policy.</strong>
      </li>
      <li>
        <strong>YouTube Data:</strong> When generating notes from YouTube videos, we may process metadata such as video title, description, and timestamps.
      </li>
      <li>
        <strong>Third-Party Advertising:</strong> AdMob may collect data for ad targeting and analytics, as described in their privacy policy.
      </li>
    </ul>

    <h2>3. How We Use Your Information</h2>
    <p>We use your information to:</p>
    <ul>
      <li>Provide and improve Cognify’s functionality and features.</li>
      <li>Analyze app performance, user trends, and the success of various note-generation methods using Firebase Analytics.</li>
      <li>Display personalized or non-personalized advertisements using AdMob’s services.</li>
      <li>Respond to support requests and provide customer service.</li>
      <li>Comply with legal obligations and ensure data security.</li>
    </ul>

    <h2>4. User Controls and Opt-Out Options</h2>
    <p>
      You have control over your data. Here’s how you can manage your preferences:
    </p>
    <ul>
      <li><strong>Ad Preferences:</strong> Users in the EEA and UK will be prompted to grant or deny consent for personalized ads. You can update your choice anytime in the app settings.</li>
      <li><strong>Opt-Out of Analytics:</strong> You can disable app analytics tracking in the app settings.</li>
      <li><strong>Delete Your Data:</strong> You can request to delete your account and data by contacting <a href="mailto:antoinegonthier11@icloud.com">antoinegonthier11@icloud.com</a>.</li>
    </ul>
    <p>Subscriptions are automatically renewed unless canceled before the end of the billing period.</p>

    <h2>5. Data Retention</h2>
    <p>
      We retain data only as long as necessary:
    </p>
    <ul>
      <li>Uploaded content is deleted immediately after text extraction.</li>
      <li>Anonymous analytics data is retained for up to 24 months.</li>
      <li>Support requests and email communications are retained for up to 12 months.</li>
    </ul>

    <h2>6. International Data Transfers</h2>
    <p>
      Data may be stored or processed in countries where our service providers operate. We ensure compliance with data protection laws through Standard Contractual Clauses (SCCs) where required.
    </p>

    <h2>7. Children's Privacy</h2>
    <p>
      Cognify is not intended for users under the age of 13. We do not knowingly collect personal information from children. If we discover that a child under 13 has provided personal data, we will delete it immediately.
    </p>

    <h2>8. Changes to This Policy</h2>
    <p>
      We may update this privacy policy from time to time. Continued use of Cognify after updates indicates your acceptance of the revised policy.
    </p>

    <p>
      This application is developed and maintained by the auto-entreprise Antoine Gonthier, located at 78 rue de la Mélinière, 44000 Nantes.
    </p>

    <p>If you have questions about this Privacy Policy, contact us at <a href="mailto:antoinegonthier11@icloud.com">antoinegonthier11@icloud.com</a>.</p>
  </div>
</template>

<script>
export default {
  name: "PrivacyPolicy",
};
</script>

<style scoped>
.privacy {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  line-height: 1.6;
}

ul {
  margin-left: 20px;
}

a {
  color: #007bff;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}
</style>